import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatExpansionPanelActionRow } from '@angular/material/expansion';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Policy, PolicyPrice, PolicyRow } from '../../models/Policy';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Logger } from '../../../../infrastructure/logging/logger';
import { Observable, startWith, map } from 'rxjs';
import { Currency } from '../../../currencies/models/Currency';
import { CurrenciesService } from '../../../currencies/services/currencies.service';
import { PriceRatesService } from '../../../price-rates/services/price-rates.service';
import { PolicyService } from '../../services/policy.service';

@Component({
  selector: 'app-assign-policy-dialog',
  templateUrl: './assign-policy-dialog.component.html',
  styleUrls: ['./assign-policy-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatIcon,
    MatButtonModule,
    MatDialogModule,
    MatExpansionPanelActionRow
  ]
})
export class AssignPolicyDialogComponent implements OnInit, AfterViewInit {

  priceTypesArray: any[] = [
    { id: 1, display_text: 'Underwriter' },
    { id: 2, display_text: 'Property Manager' }
  ]

  pricingModels: any[] = [
    { id: 1, display_text: 'Tiered Nights' },
    { id: 2, display_text: 'Per Night' }
  ]

  priceRateForm!: FormGroup;
  tieredNightForm!: FormGroup;
  perNightForm!: FormGroup;
  selectedPolicy!: Policy;
  availablePolicies: PolicyRow[] = [];
  availablePriceRates: PolicyPrice[] = [];
  currencies!: Currency[];

  enterNewPriceRateForm: boolean = false;
  numberOfNightsToInput: number = 0;
  assignOrganizationPolicy: boolean = false;
  assignPropertyPolicy: boolean = false;
  policyControl = new FormControl(undefined, [Validators.required, this.requirePolicyMatch.bind(this)]);
  filteredPolicies!: Observable<any[]>;
  priceRateControl = new FormControl(undefined, [Validators.required, this.requirePriceRateMatch.bind(this)]);
  filteredPriceRates!: Observable<any[]>;
  policySelect: Policy | undefined;
  priceRateSelect!: PolicyPrice | undefined;

  invalidForm: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AssignPolicyDialogComponent>,
    private formBuilder: FormBuilder,
    private priceRatesService: PriceRatesService,
    private currenciesService: CurrenciesService,
    private policyService: PolicyService,
    private logService: Logger
  ) { }

  ngOnInit(): void {
    this.availablePolicies = this.data.availablePolicies;
    this.availablePriceRates = this.data.availablePriceRates;
    this.selectedPolicy = this.data.policyToUpdate;
    this.loadData();

    if (this.selectedPolicy.id) {
      this.loadFromPolicy(this.selectedPolicy);

      let policyRow = this.policyService.getPolicyRowFromPolicy(this.selectedPolicy);
      if (!this.availablePolicies.some(policy => policy.id === policyRow.insurancePolicyId)) {
        this.availablePolicies.push(policyRow);
      }
    }

    if (this.availablePolicies.length === 0) {
      this.policyControl.disable();
    }

    if (this.data.component === 'organization-detail') {
      this.assignOrganizationPolicy = true;
    }
    if (this.data.component === 'properties-detail') {
      this.assignPropertyPolicy = true;
      this.priceTypesArray.splice(1, 1);
    }
  }

  ngAfterViewInit() {
    this.filteredPolicies = this.policyControl.valueChanges.pipe(
      startWith(''),
      map(value => this._policyFilter(value || '')),
    );

    this.filteredPriceRates = this.priceRateControl.valueChanges.pipe(
      startWith(''),
      map(value => this._priceRateFilter(value || '')),
    );

    this.invalidForm = false;
  }

  private requirePolicyMatch(control: FormControl): ValidationErrors | null {
    const selection = control.value;
    if (this.availablePolicies && this.availablePolicies.indexOf(selection) < 0) {
      this.invalidForm = true;
      return { requireMatch: true };
    } else {
      this.invalidForm = false;
    }
    return null;
  }

  private requirePriceRateMatch(control: FormControl): ValidationErrors | null {
    const selection: any = control.value;
    if (this.availablePriceRates && this.availablePriceRates.indexOf(selection) < 0) {
      this.invalidForm = true;

      return { requireMatch: true };
    } else {
      this.invalidForm = false;
    }
    return null;
  }

  private _policyFilter(value: string): PolicyRow[] {
    return this.availablePolicies.filter(policy => policy.name.toLowerCase().includes(value.toString().toLowerCase()));
  }

  private _priceRateFilter(value: any): PolicyPrice[] {
    return this.availablePriceRates.filter(rate => rate.name.toLowerCase().includes(value.toString().toLowerCase()));
  }

  loadFromPolicy(policy: any) {
      this.policyControl.setValue(policy.policy);
      this.policySelect = policy.policy;

    let selectedPriceRate: any = this.availablePriceRates.find(rate => rate.id === policy.policy_price_id);
    this.priceRateControl.setValue(selectedPriceRate);
    this.priceRateSelect = selectedPriceRate;
  }

  async loadData() {
    const currenciesResponse = await this.currenciesService.getCurrencies();
    this.currencies = currenciesResponse;
  }

  initPriceRateForm() {
    this.initSubPriceRateForms();
    this.priceRateForm = this.formBuilder.group({
      name: ['', Validators.required],
      type_id: ['', Validators.required],
      pricing_model_id: ['', Validators.required],
      currency_id: ['', Validators.required],
      deductible: ['', Validators.required],
      description: ['']
    });
  }

  initSubPriceRateForms() {
    this.tieredNightForm = this.formBuilder.group({
      night_ranges: this.formBuilder.array([
        this.formBuilder.group({
          name: ['', Validators.required],
          number_of_nights_from: [{ value: 1, disabled: true }, [Validators.required]],
          number_of_nights_to: ['', Validators.required],
          price: ['', Validators.required]
        })
      ]),
      extra_night_price: ['', Validators.required]
    });

    this.perNightForm = this.formBuilder.group({
      nightly_price: ['', Validators.required]
    });
  }

  submitAssignPolicy() {
    if (this.enterNewPriceRateForm) {
      this.buildPriceRatePayload();
    } else {
      let payload;
      if (this.assignOrganizationPolicy) {
        payload = this.buildOrganizationPolicyPayload();
        this.dialogRef.close(payload);
        this.resetSelects();
      }
      if (this.assignPropertyPolicy) {
        payload = this.buildPropertyPolicyPayload();
        this.dialogRef.close(payload);
        this.resetSelects();
      }
    }
  }

  async buildPriceRatePayload() {
    try {
      let priceRate: any = {
        ...this.priceRateForm.value
      }

      if (this.priceRateForm.get('pricing_model_id')?.value === '1') {
        priceRate = { ...priceRate, ...this.tieredNightForm.getRawValue() }
        delete priceRate.perNightForm;
      }
      if (this.priceRateForm.get('pricing_model_id')?.value === '2') {
        priceRate.price = this.perNightForm.get('nightly_price')?.value;
        delete priceRate.tieredNightForm;
      }

      const priceRateId = (await this.priceRatesService.createPriceRate(priceRate)).id;

      let payload;
      if (this.assignOrganizationPolicy) {
        payload = this.buildOrganizationPolicyPayload(priceRateId);
      }
      if (this.assignPropertyPolicy) {
        payload = this.buildPropertyPolicyPayload(priceRateId);
      }
      
      this.dialogRef.close(payload);
      this.resetSelects();
      this.priceRateForm.reset();
      this.tieredNightForm.reset();
      this.perNightForm.reset();
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while submitting Price Rate', 'Error while submitting Price Rate');
    }
  }

  buildOrganizationPolicyPayload(priceRateId?: number) {
    let createOrgPolicyByComponentPayload = {
      policy: this.policySelect,
      policyPrice: priceRateId ? priceRateId : this.priceRateSelect?.id
    }
    return createOrgPolicyByComponentPayload;
  }

  buildPropertyPolicyPayload(priceRateId?: number) {
    let createPropertyPolicyByComponentPayload = {
      propertyId: this.data.propertyId,
      policy: this.policySelect,
      policyPrice: priceRateId ? priceRateId : this.priceRateSelect?.id
    }
    return createPropertyPolicyByComponentPayload;
  }

  onEnterNewPriceRateClick() {
    this.initPriceRateForm();
    this.enterNewPriceRateForm = true;
  }

  nightRanges(): FormArray {
    return this.tieredNightForm.get('night_ranges') as FormArray;
  }

  newNightRange(): FormGroup {
    return this.formBuilder.group({
      name: ['', Validators.required],
      number_of_nights_from: [Number(this.numberOfNightsToInput) + 1, Validators.required],
      number_of_nights_to: ['', Validators.required],
      price: ['', Validators.required]
    });
  }

  addNightRange() {
    this.nightRanges().push(this.newNightRange());
  }

  removeNightRange(i: number) {
    this.nightRanges().removeAt(i);
  }

  setTieredNightName(i: number, nightsTo: any) {
    this.numberOfNightsToInput = nightsTo;

    let nightRangeArray = this.tieredNightForm.get("night_ranges") as FormArray;
    nightRangeArray.controls[i].patchValue({
      name: nightsTo + '-nights'
    });

    nightRangeArray.controls[i + 1].patchValue({
      number_of_nights_from: Number(nightsTo) + 1
    });
  }

  onPolicySelectionChanged(event: MatAutocompleteSelectedEvent) {
    this.policySelect = event.option.value;
  }

  onPriceRateSelectionChanged(event: MatAutocompleteSelectedEvent) {
    this.priceRateSelect = event.option.value;
  }

  canAssignPolicy(): boolean {
    if (!this.invalidForm) {
      if (!this.enterNewPriceRateForm) {
        if (this.policySelect && this.priceRateSelect || this.selectedPolicy.id) {
          return true;
        } else {
          return false;
        }
      } else {
        return (
          this.policySelect &&
          this.priceRateForm.valid && 
          this.tieredNightForm.valid || 
          this.perNightForm.valid
        )
      }
    } else {
      return false;
    }
  }

  displayPolicyFn(policy: PolicyRow): string {
    return policy && policy.name ? policy.name : '';
  }

  displayPriceRateFn(priceRate: PolicyPrice): string {
    return priceRate && priceRate.name ? priceRate.name : '';
  }

  cancelAddPolicy() {
    this.resetSelects();
    this.dialogRef.close(false);
  }

  resetSelects() {
    this.policySelect = undefined;
    this.priceRateSelect = undefined;
  }
}